import { useState, useCallback, useContext } from 'react';
import { ProductService, ProductServiceService, PaginatedProductServiceResponse } from './productService.service';
import { AuthContext } from '../../../context/authcontext';

interface PaginationData {
  currentPage: number;
  totalPages: number;
  totalCount: number;
  hasMore: boolean;
  pageSize: number;
}

interface UseProductServicesReturn {
  productServices: ProductService[];
  loading: boolean;
  backgroundLoading: boolean;
  error: string | null;
  pagination: PaginationData;
  fetchProductServices: (page?: number, pageSize?: number, filters?: Record<string, any>) => Promise<void>;
  refreshProductServices: () => Promise<void>;
  addProductService: (productService: ProductService) => void;
  updateProductService: (productService: ProductService) => void;
  removeProductService: (productServiceId: number) => void;
  lastFetched: Date | null;
  isInitialized: boolean;
  clearBackgroundLoading: () => void;
  clearProductServicesData: () => void;
}

export const useProductServices = (): UseProductServicesReturn => {
  const { selectedTenantId } = useContext(AuthContext);
  const [productServices, setProductServices] = useState<ProductService[]>([]);
  const [loading, setLoading] = useState(false);
  const [backgroundLoading, setBackgroundLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastFetched, setLastFetched] = useState<Date | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [pagination, setPagination] = useState<PaginationData>({
    currentPage: 1,
    totalPages: 1,
    totalCount: 0,
    hasMore: false,
    pageSize: 10
  });

  const [fetchedPages, setFetchedPages] = useState<Set<number>>(new Set());
  const [pageData, setPageData] = useState<Map<number, ProductService[]>>(new Map());

  const clearBackgroundLoading = useCallback(() => {
    setBackgroundLoading(false);
  }, []);

  const fetchProductServices = useCallback(async (page: number = 1, pageSize: number = 10, filters?: Record<string, any>) => {
    try {
      setLoading(true);
      setError(null);
      
      const response: PaginatedProductServiceResponse = await ProductServiceService.getProductServicesByPage(selectedTenantId!, page, pageSize, filters);
      
      setPagination({
        currentPage: page,
        totalPages: response.totalPages,
        totalCount: response.count,
        hasMore: response.hasMore,
        pageSize: response.pageSize
      });

      setPageData(prev => new Map(prev).set(page, response.results));
      setFetchedPages(prev => new Set(prev).add(page));

      if (page === 1) {
        setProductServices(response.results);
      } else {
        setProductServices(response.results);
      }

      setLastFetched(new Date());
      setIsInitialized(true);
      setBackgroundLoading(true);
    } catch (err) {
      console.error('Error fetching product services:', err);
      setError('Failed to fetch product services');
    } finally {
      setLoading(false);
    }
  }, []);

  const refreshProductServices = useCallback(async () => {
    setFetchedPages(new Set());
    setPageData(new Map());
    setIsInitialized(false);
    await fetchProductServices(1, 10);
  }, [fetchProductServices]);

  const clearProductServicesData = useCallback(() => {
    setProductServices([]);
    setError(null);
    setIsInitialized(false);
    setFetchedPages(new Set());
    setPageData(new Map());
    setPagination({
      currentPage: 1,
      totalPages: 1,
      totalCount: 0,
      hasMore: false,
      pageSize: 10
    });
  }, []);

  const addProductService = useCallback((productService: ProductService) => {
    setProductServices(prev => [productService, ...prev]);
    setPagination(prev => ({
      ...prev,
      totalCount: prev.totalCount + 1
    }));
    setFetchedPages(new Set());
    setPageData(new Map());
  }, []);

  const updateProductService = useCallback((updatedProductService: ProductService) => {
    setProductServices(prev => 
      prev.map(productService => 
        productService.product_n_service_id === updatedProductService.product_n_service_id ? updatedProductService : productService
      )
    );
    setPageData(prev => {
      const newPageData = new Map(prev);
      newPageData.forEach((productServices, pageNum) => {
        const updatedProductServices = productServices.map(productService =>
          productService.product_n_service_id === updatedProductService.product_n_service_id ? updatedProductService : productService
        );
        newPageData.set(pageNum, updatedProductServices);
      });
      return newPageData;
    });
  }, []);

  const removeProductService = useCallback((productServiceId: number) => {
    setProductServices(prev => prev.filter(productService => productService.product_n_service_id !== productServiceId));
    setPagination(prev => ({
      ...prev,
      totalCount: Math.max(0, prev.totalCount - 1)
    }));
    setFetchedPages(new Set());
    setPageData(new Map());
  }, []);

  return {
    productServices,
    loading,
    backgroundLoading,
    error,
    pagination,
    fetchProductServices,
    refreshProductServices,
    addProductService,
    updateProductService,
    removeProductService,
    lastFetched,
    isInitialized,
    clearBackgroundLoading,
    clearProductServicesData
  };
};
